import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Observable, map, switchMap, of } from 'rxjs';
import { ApiService } from './api.service';
import { AccountService } from './account.service';
import { GetImage } from '../helpers/getImage';
import { JwtHelperService } from '@auth0/angular-jwt';
import { calculateDays } from '../helpers';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  userData: any;
  img = null;
  user: any;

  constructor(
    private readonly accountService: AccountService,
    private _api: ApiService,
    private getImage: GetImage,
    private sanitizer: DomSanitizer,
    private jwtHelper: JwtHelperService,
  ) {}

  getUserDetails(reload = true) {
    let user: any = localStorage.getItem('user_data_automap');
    user = JSON.parse(user);
    this.userData = {
      email: user?.username,
      userId: user?.id,
      name: user?.username ? user.username : (user?.id ?? ''),
      uriLabel: user?.id,
    };

    if (reload) {
      this.getUserInfo(this.userData.userId);
    }

    return this.userData;
  }

  getUserInfo(userId: string) {
    //Error handling for edge cases
    if (userId === undefined) {
      this.user = {
        email: 'None',
        userId: -1,
        name: 'Dummy user',
        uriLabel: -1,
        img: 'null',
      };
      return;
    }
    this.accountService.getUserDetails(userId).subscribe((data) => {
      this.user = data;
    });
  }

  getUserClaim() {
    return this.jwtHelper.decodeToken(localStorage.getItem('access_token_automap'));
  }

  processUserExpiration() {
    const userClaim = this.getUserClaim();
    const days = calculateDays(this.user?.planExpiration) ?? 0;

    if (days <= 0 && userClaim['termhub:plan'] === 'GRACE_PERIOD') {
      return 'GRACEPERIOD';
    } else if (days <= 0 && userClaim['termhub:plan'] === 'EXPIRED') {
      return 'EXPIRED';
    } else if (days <= 30) {
      return 'WARNING';
    } else return '';
  }

  getUserIcon(id: string) {
    if (!id) return;
    this.accountService.downloadUserIconImage(id).subscribe((data) => {
      if (data) {
        this.img = this.createImageFromBlob(data);
      } else {
        this.img = null;
      }
    });
  }

  createImageFromBlob(image: Blob) {
    if (image) {
      const unsafeImageUrl = URL?.createObjectURL(image);
      return this.sanitizer?.bypassSecurityTrustUrl(unsafeImageUrl);
    }
    return null;
  }

  flush() {
    this.userData = {};
    this.img = null;
  }

  getAllUsers(query?: any, offset?: any, limit?: any, sort?: any, ascending = true): Observable<any> {
    let isQuery = '';
    const eq = query ? encodeURIComponent(query) : '';
    if (offset || limit || query || sort || ascending) {
      isQuery = `?query=${eq}&offset=${offset || 0}&limit=${limit || 10}&${sort ? `sort=${sort}&` : ''}ascending=${ascending}`;
    } else {
      isQuery = '';
    }
    return this._api.get(`/user${isQuery}`).pipe(
      map((users: any) => {
        users?.items?.map((user) => {
          user.nick =
            user.name?.substring(0, 2).toUpperCase() || user.username?.substring(0, 2).toUpperCase() || user.username?.substring(0, 2).toUpperCase();
        });
        return users;
      }),
    );
  }

  // ACCEPTS user role ADMIN or USER only
  updateUserData(id: string, data: any): Observable<any> {
    return this._api.patch(`/user/${id}`, data).pipe(
      map((user: any) => {
        user.nick =
          user.name?.substring(0, 2).toUpperCase() || user.uriLabel?.substring(0, 2).toUpperCase() || user.username?.substring(0, 2).toUpperCase();
        this.getImage.user(user.id).subscribe(
          (imageUrl) => {
            user.image = imageUrl;
          },
          (error) => {
            console.log(error);
            user.image = null;
          },
        );
      }),
    );
  }

  /**
   * @param Offset - offset is 0 by default
   * @param limit - limit is 10 by default
   */
  getUsersFor(
    usersFor: 'project' | 'organization' | 'team',
    id: string,
    query?: string,
    offset?: number,
    limit?: number,
    sort?: string,
    ascending = true,
  ): Observable<any> {
    let isQuery = '';
    const eq = query ? encodeURIComponent(query) : '';
    if (offset || limit || query || sort || ascending) {
      isQuery = `?query=${eq}&offset=${offset || 0}&limit=${limit || 10}&${sort ? `sort=${sort}&` : ''}ascending=${ascending}`;
    } else {
      isQuery = '';
    }
    return this._api.get(`/${usersFor}/${id}/users${isQuery}`).pipe(
      map((users: any) => {
        users?.items?.map((user) => {
          user.nick = user?.name ? user.name?.substring(0, 2).toUpperCase() : user.uriLabel?.substring(0, 2).toUpperCase();
          this.getImage.user(user.id).subscribe(
            (imageUrl) => {
              user.image = imageUrl;
            },
            (error) => {
              user.image = null;
            },
          );
        });
        return users;
      }),
    );
  }

  // REMOVE USER FROM PROJECT
  removeUserFromProject(projectId: string, userId: string, userRole: string) {
    return this._api.delete(`/project/${projectId}/${userRole}/user/${userId}`).pipe(map((data) => data));
  }

  // ADD USER TO PROJECT
  addUserToProject(projectId: string, userId: string, userRole: string) {
    return this._api.post(`/project/${projectId}/${userRole}/user`, userId).pipe(map((data) => data));
  }

  // Verify User by email
  verifyUser(email: string): Observable<any> {
    return this._api.get(`/user?query=username:${encodeURIComponent(email)}&offset=0&limit=1`, null).pipe(
      switchMap((data: any) => {
        if (data.total > 0) {
          return this._api.post(`/user/check?username=${encodeURIComponent(email)}&verified=true`, null).pipe(
            map((user: any) => {
              if (user.verified) {
                return { valid: true, id: data.items[0].id };
              } else {
                return { valid: false };
              }
            }),
          );
        } else {
          return of({ valid: false });
        }
      }),
    );
  }

  // DELETE USER
  deleteUser(userId, cascade = false): Observable<any> {
    return this._api.delete(`/user/${userId}${cascade ? '?cascade=true' : ''}`).pipe(map((data) => data));
  }

  // ASSUME USER
  assumeUser(userId: string): Observable<any> {
    return this._api.post(`/auth/proxy?expiresIn=14400`, { 'termhub:id': userId }).pipe(map((data) => data));
  }

  ChangeProjectUserRole(projectId: string, currentRole: string, userId: string, newRole: string): Observable<any> {
    return this._api.put(`/project/${projectId}/${currentRole}/user/${userId}`, newRole).pipe(map((data) => data));
  }
  KpiMetrics(role: string): Observable<any> {
    return this._api.get(`/${role}?offset=0&limit=1`).pipe(map((data) => data));
  }

  //POST user - admin adds new user
  postUser(username, password, role): Observable<any> {
    return this._api.post(`/user`, { username: username, password: password, role: role, organizationId: username }).pipe(map((data) => data));
  }

  getSignupUsers(offset?: number, limit?: number, ascending: boolean = true): Observable<any> {
    return this._api
      .get(`/terminology/config?type=signup&offset=${offset}&limit=${limit}&ascending=${ascending}&sort=created`)
      .pipe(map((data) => data));
  }
}
